import { type JSX,useCallback, useEffect, useLayoutEffect, useState } from 'react';

import { Stack } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { oemTenantId } from 'app/config';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { useDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { EditDrawer } from 'common/components/EditDrawer';
import {
    selectCurrentEntity,
    setNameInConflict,
    setSaveButtonEnabled
} from 'common/components/EditDrawer/editDrawerSlice';
import { EntitiesMultipleSelect } from 'common/components/EntitiesMultipleSelect';
import { ErrorHandler } from 'common/components/errorHandler/ErrorHandler';
import { getOptionLabelDefaultCallback, isOptionEqualToValueDefaultCallback } from 'common/components/sharedCustomComponents/Common/helpers';
import { EntitiesSearchableCommonDropDown } from 'common/components/sharedCustomComponents/EntitiesSearchableCommonDropDown/EntitiesSearchableCommonDropDown';
import { ExpandableTextArea, ExpandableTextAreaProps } from 'common/components/sharedCustomComponents/ExpandableTextArea/ExpandableTextArea';
import { PalmDatePicker } from 'common/components/sharedCustomComponents/PalmDatePicker/PalmDatePicker';
import { ReadOnlyTextField } from 'common/components/sharedCustomComponents/ReadOnlyTextField/ReadOnlyTextField';
import { Roles } from 'common/constants/roles';
import { useAuthContext } from 'common/contexts/AuthContext';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchApplicationSuites } from 'common/services/api/ApplicationSuites/ApplicationSuites.thunks';
import { fetchGlobalRoles } from 'common/services/api/GlobalRoles/GlobalRoles.thunks';
import { fetchTenants } from 'common/services/api/Tenants/Tenants.thunks';
import {
    APITenantUserMembershipPostParams,
    APITenantUserMembershipPostResponse,
    APITenantUserMembershipPutParams,
    APITenantUserMembershipPutResponse
} from 'common/services/api/TenantUserMembership/TenantUserMembershipApiTypeDefs';
import { postUserProfileTenantMembership, putUserProfileTenantMembership } from 'common/services/api/UserProfileApps/UserProfileApps.thunk';
import { fetchUserProfileTenant } from 'common/services/api/UserProfileGlobalRoles/UserProfileGlobalRoles.thunk';
import { ApplicationSuite,AppSuiteTenant, GlobalRole } from 'common/types/Entities';
import { DrawerEntitiesNames, EditDrawerContentProps,Operations } from 'common/types/TypeDefs';
import { displayAppSuiteOptionMenuItemContent } from 'common/utils/AppSuiteEntitiesDropDownHelper';
import { dateToFrontEndFormat, isStartDateBeforeEndDate, toESTDateString } from 'common/utils/dateHelpers';
import { disableIfMissingAnyRole as disableFieldEvaluator } from 'common/utils/DisableButtonEvaluator';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { DEFAULT_ERROR_MESSAGE, handleListFetchError } from 'common/utils/errorHandlers';
import { displayTenantOptionMenuItemContent } from 'common/utils/TenantEntitiesDropDownHelper';
import { selectGlobalRolesByTenantId } from 'features/globalRoles/globalRolesSlice';
import { selectUserProfileGlobalRolesByTenantId } from 'features/userProfileGlobalRoles/userProfileGlobalRolesSlice';
import { selectUserDetails } from 'features/users/usersSlice';
import { useSnackbar } from 'notistack';

import { updateUserProfileTenant } from './helpers';
import { IUserProfileTenant } from './UserProfileApps';
import { clearAppTenants, selectAppSuites, selectTenants } from './UserProfileAppsTenantsSlice';

const renderGlobalRoleName = (entity: GlobalRole) => <>{entity.name}</>;

const DEFAULT_APP_SUITE = { id: '', name: '', tenantsCount: 0 };
const DEFAULT_TENANT = { id: '', name: '', applicationSuiteId: '', isLicenseRequired: false };

interface IDatesValidation {
    hasError: boolean;
    errorMessage: string;
}

interface ProfileAppsTenantsItem {
    appSuiteName: string;
    tenant: IUserProfileTenant;
    startDate?: string;
    endDate?: string;
}

interface ProfileAppsTenantsDrawerProperties {
    userId: string;
    userFirstName: string;
    userLastName: string;
    updatePageCallback: () => void;
    handleClose: () => void;
}

function useProfileAppsTenantsDrawer(
    entityName: string,
    drawerProps: ProfileAppsTenantsDrawerProperties,
    editingItemInfo?: EditDrawerContentProps<ProfileAppsTenantsItem>['editingItemInfo']
) {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [tenant, setTenant] = useState<AppSuiteTenant | null>(DEFAULT_TENANT);
    const [notes, setNotes] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [appSuite, setAppSuite] = useState<ApplicationSuite | null>(DEFAULT_APP_SUITE);
    const [currentGlobalRoles, setCurrentGlobalRoles] = useState<string[]>([]);
    const [datesValidation, setDatesValidation] = useState<IDatesValidation>({
        hasError: false,
        errorMessage: 'The end date must be greater than the start date.'
    });

    const drawerContext = useDrawerContext<
        ProfileAppsTenantsItem,
        ProfileAppsTenantsDrawerProperties,
        APITenantUserMembershipPostParams,
        APITenantUserMembershipPostResponse,
        APITenantUserMembershipPutParams,
        APITenantUserMembershipPutResponse
    >(entityName, drawerProps, editingItemInfo);

    const appSuitesOptions = useAppSelector(selectAppSuites);
    const tenantsOptions = useAppSelector(selectTenants);
    const currentUserRoles = useAuthContext().currentUserRoles;
    const allowedRolesToModifyGlobalRolesField = [Roles.GlobalRoleManager, Roles.Admin];
    const shouldDisableGlobalRoleField = disableFieldEvaluator(currentUserRoles, allowedRolesToModifyGlobalRolesField);
    const userProfileGlobalRoles = useAppSelector(selectUserProfileGlobalRolesByTenantId(editingItemInfo?.editingRow?.tenant.id ?? ''));
    const globalRoles = useAppSelector(selectGlobalRolesByTenantId(oemTenantId === tenant?.id));

    useEffect(() => {
        if (drawerContext.currentId === DrawerIDs.UserProfileAppTenants || drawerContext.currentId === DrawerIDs.GlobalRoles) {
            if (userProfileGlobalRoles || !editingItemInfo?.editingRow) {
                return;
            }
            const fetchUserProfileTenantParams = {
                tenantId: editingItemInfo?.editingRow.tenant.id,
                userId: drawerProps.userId
            };
            dispatch(fetchUserProfileTenant(fetchUserProfileTenantParams))
                .unwrap()
                .then(res => {
                    setCurrentGlobalRoles(res.globalRoles?.map(gr => gr?.id) || []);
                })
                .catch((error) => {
                    handleListFetchError(extractStatusCode(error.message), 'User Profile Global Roles', enqueueSnackbar);
                });
        }
    }, [drawerContext.isOpenDrawer, drawerContext.currentId, userProfileGlobalRoles, editingItemInfo?.editingRow, drawerProps.userId, dispatch, enqueueSnackbar]);

    useLayoutEffect(() => {
        if (drawerContext.currentId === DrawerIDs.UserProfileAppTenants || drawerContext.currentId === DrawerIDs.GlobalRoles) {
            if (!globalRoles.length) {
                dispatch(fetchGlobalRoles()).catch(console.error);
            }

            setAppSuite(DEFAULT_APP_SUITE);
            setDatesValidation(prev => ({
                hasError: false,
                errorMessage: prev.errorMessage
            }));

            if (drawerContext.action === Operations.Edit && editingItemInfo?.editingRow) {
                setTenant(editingItemInfo?.editingRow.tenant);
                setCurrentGlobalRoles(userProfileGlobalRoles?.map(gr => gr?.id) || []);
                setNotes(editingItemInfo?.editingRow.tenant.notes ?? '');
                setStartDate(editingItemInfo?.editingRow?.tenant?.startDate ?? '');
                setEndDate(editingItemInfo?.editingRow?.tenant?.endDate ?? '');

                dispatch(setSaveButtonEnabled(true));
            } else {
                const today = dateToFrontEndFormat(toESTDateString(new Date()));

                setTenant(DEFAULT_TENANT);
                setCurrentGlobalRoles([]);
                setNotes('');
                setStartDate(today);
                setEndDate('');

                dispatch(setSaveButtonEnabled(false));
            }

            dispatch(clearAppTenants());
            dispatch(setNameInConflict(false));
            if (!drawerContext.isOpenDrawer) {
                drawerProps.handleClose();
            }
        }
    }, [drawerContext.isOpenDrawer, drawerContext.currentId, drawerContext.action, dispatch]);

    useEffect(() => {
        const hasError = isStartDateBeforeEndDate(startDate, endDate) === false;
        setDatesValidation(prev => ({
            hasError: hasError,
            errorMessage: prev.errorMessage
        }));
    }, [startDate, endDate]);

    useEffect(() => {
        if (drawerContext.currentId === DrawerIDs.UserProfileAppTenants || drawerContext.currentId === DrawerIDs.GlobalRoles) {
            const requiredFields = {
                tenant: tenant?.id,
                startDate: startDate
            };
            const requiredFieldsAreEmpty = !Object
                .values(requiredFields)
                .every(fieldValue => !!fieldValue);

            if (requiredFieldsAreEmpty || datesValidation.hasError) {
                dispatch(setSaveButtonEnabled(false));
            } else {
                dispatch(setSaveButtonEnabled(true));
            }
        }
    }, [
        drawerContext.isOpenDrawer,
        drawerContext.currentId,
        startDate,
        endDate,
        editingItemInfo?.editingItemId,
        currentGlobalRoles,
        tenant?.id,
        datesValidation.hasError,
        dispatch
    ]);

    const formatDate = useCallback((date?: string) => {
        if (!date) return '';
        if (!date.includes('/')) return date;

        const [month, day, year] = date.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }, []);

    const handleApplicationSuiteChange = useCallback((_e: unknown, newAppSuite: ApplicationSuite | null) => {
        setAppSuite(newAppSuite);
        setTenant(DEFAULT_TENANT);
        setCurrentGlobalRoles([]);
        setNotes('');
        dispatch(setSaveButtonEnabled(false));
    }, [dispatch]);

    const handleTenantChange = useCallback((_e: unknown, newTenant: AppSuiteTenant | null) => {
        setCurrentGlobalRoles([]);
        setTenant(newTenant);
        dispatch(setSaveButtonEnabled(!!newTenant?.id));
        dispatch(setNameInConflict(false));
    }, [dispatch]);

    const getPostOptions = useCallback((): APITenantUserMembershipPostParams => {
        const body = {
            tenantId: tenant?.id ?? '',
            userId: drawerProps.userId,
            notes: notes || '',
            globalRoles: currentGlobalRoles.map(x => ({
                id: x
            })),
            startDate: formatDate(startDate)
        };

        if (endDate) {
            return { ...body, endDate: formatDate(endDate) };
        }

        return body;
    }, [tenant?.id, drawerProps.userId, notes, currentGlobalRoles, startDate, endDate, formatDate]);

    const getPutOptions = useCallback((): APITenantUserMembershipPutParams => {
        const body = {
            tenantId: editingItemInfo?.editingRow?.tenant.id ?? '',
            userId: drawerProps.userId,
            notes: notes || '',
            globalRoles: currentGlobalRoles.map(x => ({
                id: x
            })),
            startDate: formatDate(startDate)
        };

        if (endDate) {
            return { ...body, endDate: formatDate(endDate) };
        }

        return body;
    }, [editingItemInfo?.editingRow?.tenant.id, drawerProps.userId, notes, currentGlobalRoles, startDate, endDate, formatDate]);

    const createSuccessHandler = useCallback((postResult: APITenantUserMembershipPostResponse) => {
        drawerContext.createSuccessHandler(postResult);
        drawerProps.updatePageCallback();
        const postOptions = getPostOptions();
        updateUserProfileTenant(postOptions);
    }, [drawerContext, drawerProps, getPostOptions]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const createFailureHandler = useCallback((error: any) => {
        dispatch(setNameInConflict(false));
        if (extractStatusCode(error.message) === 409) {
            dispatch(setNameInConflict(true));
        } else {
            enqueueSnackbar(
                'There was an error while linking the Tenant.' +
                DEFAULT_ERROR_MESSAGE,
                { variant: 'error' }
            );
        }
    }, [dispatch, enqueueSnackbar]);

    const editSuccessHandler = useCallback((putResult: APITenantUserMembershipPutResponse) => {
        drawerContext.editSuccessHandler(putResult);
        const putOptions = getPutOptions();
        updateUserProfileTenant(putOptions);
    }, [drawerContext, getPutOptions]);

    const editFailureHandler = useCallback(() => {
        dispatch(setNameInConflict(false));
        enqueueSnackbar(
            'There was an error while editing the Tenant Membership.' +
            DEFAULT_ERROR_MESSAGE,
            { variant: 'error' }
        );
    }, [dispatch, enqueueSnackbar]);

    const createUserTenantMembershipNotesTextAreaContext = useCallback((): ExpandableTextAreaProps => {
        return {
            textAreaCommonProps: {
                value: notes,
                onChange: setNotes,
                placeholder: 'Type Notes',
                label: 'Notes'
            },
            popoutCommonProps: {
                popoutInputBaseProps: {
                    inputBasePlaceholder: 'Type Tenant Membership'
                },
                popoutInputLabel: 'Tenant Membership Notes (optional)'
            }
        };
    }, [notes]);

    const postCallback = useCallback(() => {
        drawerContext.postCallback(postUserProfileTenantMembership, getPostOptions, createSuccessHandler, createFailureHandler);
    }, [drawerContext, getPostOptions, createSuccessHandler, createFailureHandler]);

    const putCallback = useCallback(() => {
        drawerContext.putCallback(putUserProfileTenantMembership, getPutOptions, editSuccessHandler, editFailureHandler);
    }, [drawerContext, getPutOptions, editSuccessHandler, editFailureHandler]);

    return {
        ...drawerContext,
        tenant,
        notes,
        startDate,
        endDate,
        appSuite,
        currentGlobalRoles,
        datesValidation,
        appSuitesOptions,
        tenantsOptions,
        shouldDisableGlobalRoleField,
        globalRoles,
        setStartDate,
        setEndDate,
        setCurrentGlobalRoles,
        handleApplicationSuiteChange,
        handleTenantChange,
        createUserTenantMembershipNotesTextAreaContext,
        postCallback,
        putCallback
    };
}

function CreateContent({
    processing,
    nameInConflict,
    appSuite,
    tenant,
    globalRoles,
    shouldDisableGlobalRoleField,
    startDate,
    endDate,
    datesValidation,
    handleApplicationSuiteChange,
    handleTenantChange,
    setCurrentGlobalRoles,
    setStartDate,
    setEndDate,
    createUserTenantMembershipNotesTextAreaContext,
    postCallback,
    drawerProps
}: Readonly<{
    processing: boolean;
    nameInConflict: boolean;
    appSuite: ApplicationSuite | null;
    tenant: AppSuiteTenant | null;
    currentGlobalRoles: string[];
    globalRoles: GlobalRole[];
    shouldDisableGlobalRoleField: boolean;
    startDate: string;
    endDate: string;
    datesValidation: IDatesValidation;
    handleApplicationSuiteChange: (_e: unknown, newAppSuite: ApplicationSuite | null) => void;
    handleTenantChange: (_e: unknown, newTenant: AppSuiteTenant | null) => void;
    setCurrentGlobalRoles: (globalRoles: string[]) => void;
    setStartDate: (startDate: string) => void;
    setEndDate: (endDate: string) => void;
    createUserTenantMembershipNotesTextAreaContext: () => ExpandableTextAreaProps;
    postCallback: () => void;
    drawerProps: ProfileAppsTenantsDrawerProperties;
}>): JSX.Element {
    return (
        <EditDrawer
            title={'Application and Tenant'}
            saveButtonFunction={postCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <EntitiesSearchableCommonDropDown
                    initialEntityOption={DEFAULT_APP_SUITE}
                    value={appSuite}
                    onChange={handleApplicationSuiteChange}
                    label='Application Suite'
                    getOptionLabel={getOptionLabelDefaultCallback}
                    isOptionEqualToValue={isOptionEqualToValueDefaultCallback}
                    asyncThunkAction={fetchApplicationSuites}
                    asyncThunkArgs={undefined}
                    displayMenuItemContent={displayAppSuiteOptionMenuItemContent}
                />
                <EntitiesSearchableCommonDropDown
                    initialEntityOption={DEFAULT_TENANT}
                    value={tenant}
                    disabled={!appSuite?.id}
                    onChange={handleTenantChange}
                    label='Tenant'
                    getOptionLabel={getOptionLabelDefaultCallback}
                    isOptionEqualToValue={isOptionEqualToValueDefaultCallback}
                    asyncThunkAction={fetchTenants}
                    asyncThunkArgs={{ id: appSuite?.id ?? '' }}
                    displayMenuItemContent={displayTenantOptionMenuItemContent}
                    effectDependecyArray={[appSuite?.id]}
                />
                {nameInConflict &&
                    <ErrorHandler
                        component='div'
                        display='inline'
                        errorMessage='This Tenant is already linked to this User.'
                    />
                }
                <EntitiesMultipleSelect
                    title='Global Roles'
                    onChange={setCurrentGlobalRoles}
                    disabled={!tenant?.id || shouldDisableGlobalRoleField}
                    entities={globalRoles}
                    itemRenderer={renderGlobalRoleName}
                    useInfoTooltip
                    clearDependencies={[appSuite?.id, tenant?.id]}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack display="flex" direction="row" justifyContent="space-between" spacing={2}>
                        <PalmDatePicker
                            label='Start Date'
                            onChange={(value: string) => setStartDate(value)}
                            required={true}
                            value={startDate}
                        ></PalmDatePicker>
                        <PalmDatePicker
                            label='End Date'
                            onChange={(value: string) => setEndDate(value)}
                            value={endDate}
                        ></PalmDatePicker>
                    </Stack>
                </LocalizationProvider>
                {datesValidation.hasError && (
                    <ErrorHandler
                        component='div'
                        display='inline'
                        errorMessage={datesValidation.errorMessage}
                    />
                )}
                <ExpandableTextArea {...createUserTenantMembershipNotesTextAreaContext()} />
                <ReadOnlyTextField label="User Name" value={`${drawerProps.userFirstName} ${drawerProps.userLastName}`} />
            </Stack>
        </EditDrawer>
    );
}

function EditContent({
    processing,
    currentGlobalRoles,
    globalRoles,
    shouldDisableGlobalRoleField,
    startDate,
    endDate,
    datesValidation,
    setCurrentGlobalRoles,
    setStartDate,
    setEndDate,
    createUserTenantMembershipNotesTextAreaContext,
    putCallback,
    drawerProps,
    editingItemInfo
}: Readonly<{
    processing: boolean;
    currentGlobalRoles: string[];
    globalRoles: GlobalRole[];
    shouldDisableGlobalRoleField: boolean;
    startDate: string;
    endDate: string;
    datesValidation: IDatesValidation;
    setCurrentGlobalRoles: (globalRoles: string[]) => void;
    setStartDate: (startDate: string) => void;
    setEndDate: (endDate: string) => void;
    createUserTenantMembershipNotesTextAreaContext: () => ExpandableTextAreaProps;
    putCallback: () => void;
    drawerProps: ProfileAppsTenantsDrawerProperties;
    editingItemInfo?: EditDrawerContentProps<ProfileAppsTenantsItem>['editingItemInfo'];
}>): JSX.Element {
    return (
        <EditDrawer
            title={'Tenant Membership'}
            saveButtonFunction={putCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <ReadOnlyTextField label="Application Suite" value={editingItemInfo?.editingRow?.appSuiteName} />
                <ReadOnlyTextField label="Tenant" value={editingItemInfo?.editingRow?.tenant.name} />
                <EntitiesMultipleSelect
                    title='Global Roles'
                    onChange={setCurrentGlobalRoles}
                    disabled={shouldDisableGlobalRoleField}
                    entities={globalRoles}
                    itemRenderer={renderGlobalRoleName}
                    useInfoTooltip
                    initialValue={currentGlobalRoles}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack display="flex" direction="row" justifyContent="space-between" spacing={2}>
                        <PalmDatePicker
                            label='Start Date'
                            onChange={(value: string) => setStartDate(value)}
                            required={true}
                            value={startDate}
                        ></PalmDatePicker>
                        <PalmDatePicker
                            label='End Date'
                            onChange={(value: string) => setEndDate(value)}
                            value={endDate}
                        ></PalmDatePicker>
                    </Stack>
                </LocalizationProvider>
                {datesValidation.hasError && (
                    <ErrorHandler
                        component='div'
                        display='inline'
                        errorMessage={datesValidation.errorMessage}
                    />
                )}
                <ExpandableTextArea {...createUserTenantMembershipNotesTextAreaContext()} />
                <ReadOnlyTextField label="User Name" value={`${drawerProps.userFirstName} ${drawerProps.userLastName}`} />
            </Stack>
        </EditDrawer>
    );
}

export function UserProfileAppsTenantsEditDrawer(props: Readonly<{
    editingRow: ProfileAppsTenantsItem | null,
    updatePageCallback: () => void,
    handleClose: () => void
}>) {
    const editingItemInfo: EditDrawerContentProps<ProfileAppsTenantsItem>['editingItemInfo'] = {
        editingRow: props.editingRow ?? undefined
    };
    const userDetails = useAppSelector(selectUserDetails);
    const currentEntity = useAppSelector(selectCurrentEntity);
    const profileAppsTenantsDrawerProps: ProfileAppsTenantsDrawerProperties = {
        userId: userDetails?.userId ?? '',
        userFirstName: userDetails?.firstName ?? '',
        userLastName: userDetails?.lastName ?? '',
        updatePageCallback: props.updatePageCallback,
        handleClose: props.handleClose
    };

    const drawerState = useProfileAppsTenantsDrawer('Tenant', profileAppsTenantsDrawerProps, editingItemInfo);

    if (currentEntity !== DrawerEntitiesNames.UserTenant) {
        return null;
    }

    if (drawerState.action === Operations.Create || drawerState.action === Operations.Link) {
        return (
            <CreateContent
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                appSuite={drawerState.appSuite}
                tenant={drawerState.tenant}
                currentGlobalRoles={drawerState.currentGlobalRoles}
                globalRoles={drawerState.globalRoles}
                shouldDisableGlobalRoleField={drawerState.shouldDisableGlobalRoleField}
                startDate={drawerState.startDate}
                endDate={drawerState.endDate}
                datesValidation={drawerState.datesValidation}
                handleApplicationSuiteChange={drawerState.handleApplicationSuiteChange}
                handleTenantChange={drawerState.handleTenantChange}
                setCurrentGlobalRoles={drawerState.setCurrentGlobalRoles}
                setStartDate={drawerState.setStartDate}
                setEndDate={drawerState.setEndDate}
                createUserTenantMembershipNotesTextAreaContext={drawerState.createUserTenantMembershipNotesTextAreaContext}
                postCallback={drawerState.postCallback}
                drawerProps={profileAppsTenantsDrawerProps}
            />
        );
    }

    if (drawerState.action === Operations.Edit) {
        if (!editingItemInfo) {
            return <></>;
        }
        return (
            <EditContent
                processing={drawerState.processing}
                currentGlobalRoles={drawerState.currentGlobalRoles}
                globalRoles={drawerState.globalRoles}
                shouldDisableGlobalRoleField={drawerState.shouldDisableGlobalRoleField}
                startDate={drawerState.startDate}
                endDate={drawerState.endDate}
                datesValidation={drawerState.datesValidation}
                setCurrentGlobalRoles={drawerState.setCurrentGlobalRoles}
                setStartDate={drawerState.setStartDate}
                setEndDate={drawerState.setEndDate}
                createUserTenantMembershipNotesTextAreaContext={drawerState.createUserTenantMembershipNotesTextAreaContext}
                putCallback={drawerState.putCallback}
                drawerProps={profileAppsTenantsDrawerProps}
                editingItemInfo={editingItemInfo}
            />
        );
    }

    return <></>;
}
